import { useRef } from "react";

import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { ProgressNoteSection } from "./ProgressNoteSection";

interface IProgressNoteProps {
    noteModel: IPatientMedicalNoteModel;
    isEditMode: boolean;
    isReadOnly: boolean;
    handwrittenNotes: () => JSX.Element[];
    loadCurrentVitalSign: () => JSX.Element;
    toggleAutoSave: () => void;
    toggleSectionOpen: () => void;
    showNotePopup: (noteContext: string, e: React.MouseEvent) => void;
    closeClickableText: () => void;
    onAutosaveFieldBlur: () => void;
    showClickableVisibility: (clickableText: string, e: React.MouseEvent) => void;
    addVitalSignItem: () => void;
    displayHandNoteUI: (encounterID: string) => void;
    isSectionOpen: boolean;
}

function ProgressNote(props: IProgressNoteProps): JSX.Element {
    const componentRef = useRef(null);

    const encounter = props.noteModel.get("Encounter");
    const isSignedOff = encounter.IsSignedOff;

    const allEncounterNotes = props.noteModel.get("EncounterNotes");
    const notesReason = allEncounterNotes.filter(x => x.Context === "RFV");
    const notesHistory = allEncounterNotes.filter(x => x.Context === "HPI");
    const notesExam = allEncounterNotes.filter(x => x.Context === "EXAM");
    const notesLab = allEncounterNotes.filter(x => x.Context === "LAB");
    const notesImaging = allEncounterNotes.filter(x => x.Context === "IMG");
    const notesOther = allEncounterNotes.filter(x => x.Context === "OTHN");
    const handwrittenNotes: JSX.Element[] = props.handwrittenNotes();
    const vitalSignsRender: JSX.Element = props.loadCurrentVitalSign();

    const reason = IsNullOrWhiteSpace(encounter.VisitPurpose) && notesReason.size <= 0;
    const historyOfPresentIll = IsNullOrWhiteSpace(encounter.HistoricalNote) && notesHistory.size <= 0;
    const exam = IsNullOrWhiteSpace(encounter.ExamenNote) && notesExam.size <= 0;
    const handWrittenNote = isArrayNullOrUndefinedOrEmpty(handwrittenNotes);
    const labratory = IsNullOrWhiteSpace(encounter.LabNote) && notesLab.size <= 0;
    const imaging = IsNullOrWhiteSpace(encounter.RadiologyNote) && notesImaging.size <= 0;
    const otherNotes = IsNullOrWhiteSpace(encounter.ProgressNote) && notesOther.size <= 0;

    const isEmpty = (
        reason
        && historyOfPresentIll
        && exam
        && labratory
        && imaging
        && otherNotes
        && handWrittenNote
        && allEncounterNotes.size <= 0
        && !vitalSignsRender
    );

    function handleBlur(): void {
        // Delay checking focus to allow time for the next element to gain focus
        setTimeout(() => {
            if (componentRef?.current && !componentRef.current.contains(document.activeElement)) {
                props.onAutosaveFieldBlur();
            }
        }, 0);
    }

    return (isEmpty && isSignedOff) ? null : (
        <>
            <div className="note-block medical-note-block" ref={componentRef}>
                <div className="note-block-header" onClick={() => props.toggleSectionOpen()}>
                    <div className="pull-right">
                        <span className={"glyphicon " + (props.isSectionOpen ? "glyphicon-chevron-up" : "glyphicon-chevron-down")} />
                    </div>
                    <h3>{cacheStore.GetString("EvolutionaryNoteMLabel")}</h3>
                </div>

                <div className={props.isReadOnly ? "no-hover" : ""}>
                    {props.isSectionOpen && (
                        <div className="patient-note">
                            <table className="fullWidth">
                                <tbody>
                                    {(!isSignedOff || !IsNullOrWhiteSpace(encounter.VisitPurpose) || notesReason.size > 0) && (
                                        <ProgressNoteSection
                                            title={cacheStore.GetString("ConsultReasonLabel")}
                                            encounterId={encounter.Id}
                                            itemSize={notesReason.size}
                                            noteContent={encounter.VisitPurpose}
                                            isEditMode={props.isEditMode}
                                            isReadOnly={props.isReadOnly}
                                            sectionName="reason"
                                            context="RFV"
                                            clickableTextVisible={props.noteModel.get("ReasonClickableTextVisible")}
                                            showNotePopup={(context, event) => props.showNotePopup(context, event)}
                                            closeClickableText={() => props.closeClickableText()}
                                            onAutosaveFieldBlur={() => handleBlur()}
                                            showClickableVisibility={(text, event) => props.showClickableVisibility(text, event)}
                                            toggleAutoSave={() => props.toggleAutoSave()}
                                            operation={LocalPatientOperations.MedicalNote.Edit_ReasonTextChanged}
                                        />
                                    )}
                                    {(!isSignedOff || !IsNullOrWhiteSpace(encounter.HistoricalNote) || notesHistory.size > 0) && (
                                        <ProgressNoteSection
                                            title={cacheStore.GetString("HPILabel")}
                                            encounterId={encounter.Id}
                                            itemSize={notesHistory.size}
                                            noteContent={encounter.HistoricalNote}
                                            sectionName="history"
                                            context="HPI"
                                            isEditMode={props.isEditMode}
                                            isReadOnly={props.isReadOnly}
                                            clickableTextVisible={props.noteModel.get("HistoryClickableTextVisible")}
                                            showNotePopup={(context, event) => props.showNotePopup(context, event)}
                                            closeClickableText={() => props.closeClickableText()}
                                            onAutosaveFieldBlur={() => handleBlur()}
                                            showClickableVisibility={(text, event) => props.showClickableVisibility(text, event)}
                                            toggleAutoSave={() => props.toggleAutoSave()}
                                            operation={LocalPatientOperations.MedicalNote.Edit_PresentIllnessHistoryTextChanged}
                                        />
                                    )}
                                    {(!isSignedOff || vitalSignsRender) && (
                                        <tr>
                                            <td className="c1 bottom-pad">
                                                <strong>{cacheStore.GetString("VitalSignsLabel")}</strong>
                                            </td>
                                            <td className="c2 bottom-pad">
                                                <div className="tb-row tb">
                                                    <div className="add-btn-column-summary">
                                                        {(isSignedOff || !props.isEditMode) ? "" : <a onClick={() => props.addVitalSignItem()}>[+]</a>}
                                                    </div>
                                                    <div data-cy="medicalNote-vitals">
                                                        {vitalSignsRender}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {(!isSignedOff || !IsNullOrWhiteSpace(encounter.ExamenNote) || notesExam.size > 0) && (
                                        <ProgressNoteSection
                                            title={cacheStore.GetString("ExamLabel")}
                                            encounterId={encounter.Id}
                                            itemSize={notesExam.size}
                                            noteContent={encounter.ExamenNote}
                                            isEditMode={props.isEditMode}
                                            isReadOnly={props.isReadOnly}
                                            sectionName="exam"
                                            context="EXAM"
                                            clickableTextVisible={props.noteModel.get("ExamenClickableTextVisible")}
                                            showNotePopup={(context, event) => props.showNotePopup(context, event)}
                                            closeClickableText={() => props.closeClickableText()}
                                            onAutosaveFieldBlur={() => handleBlur()}
                                            showClickableVisibility={(text, event) => props.showClickableVisibility(text, event)}
                                            toggleAutoSave={() => props.toggleAutoSave()}
                                            operation={LocalPatientOperations.MedicalNote.Edit_ExamTextChanged}
                                        />
                                    )}
                                    {(!isSignedOff || !isArrayNullOrUndefinedOrEmpty(handwrittenNotes)) && (
                                        <tr>
                                            <td className="c1 bottom-pad">
                                                <strong>{cacheStore.GetString("HandwrittenNotesLabel")}</strong>
                                            </td>
                                            <td className="c2 bottom-pad">
                                                <div className="tb-row tb">
                                                    <div className="add-btn-column">
                                                        {(isSignedOff || !props.isEditMode) ? "" : (
                                                            <a onClick={() => props.displayHandNoteUI(encounter.Id)}>
                                                                <span>[+]</span>
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div data-cy="medicalNote-handnotes" />
                                                    {isArrayNullOrUndefinedOrEmpty(handwrittenNotes) ? "" : handwrittenNotes}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {(!isSignedOff || !IsNullOrWhiteSpace(encounter.LabNote) || notesLab.size > 0) && (
                                        <ProgressNoteSection
                                            title={cacheStore.GetString("LaboratoryLabel")}
                                            encounterId={encounter.Id}
                                            itemSize={notesLab.size}
                                            noteContent={encounter.LabNote}
                                            isEditMode={props.isEditMode}
                                            isReadOnly={props.isReadOnly}
                                            sectionName="lab"
                                            context="LAB"
                                            clickableTextVisible={props.noteModel.get("LabClickableTextVisible")}
                                            showNotePopup={(context, event) => props.showNotePopup(context, event)}
                                            closeClickableText={() => props.closeClickableText()}
                                            onAutosaveFieldBlur={() => handleBlur()}
                                            showClickableVisibility={(text, event) => props.showClickableVisibility(text, event)}
                                            toggleAutoSave={() => props.toggleAutoSave()}
                                            operation={LocalPatientOperations.MedicalNote.Edit_LaboratoryTextChanged}
                                        />
                                    )}
                                    {(!isSignedOff || !IsNullOrWhiteSpace(encounter.RadiologyNote) || notesImaging.size > 0) && (
                                        <ProgressNoteSection
                                            title={cacheStore.GetString("RadiologyLabel")}
                                            encounterId={encounter.Id}
                                            itemSize={notesImaging.size}
                                            noteContent={encounter.RadiologyNote}
                                            isEditMode={props.isEditMode}
                                            isReadOnly={props.isReadOnly}
                                            sectionName="imaging"
                                            context="IMG"
                                            clickableTextVisible={props.noteModel.get("ImagingClickableTextVisible")}
                                            showNotePopup={(context, event) => props.showNotePopup(context, event)}
                                            closeClickableText={() => props.closeClickableText()}
                                            onAutosaveFieldBlur={() => handleBlur()}
                                            showClickableVisibility={(text, event) => props.showClickableVisibility(text, event)}
                                            toggleAutoSave={() => props.toggleAutoSave()}
                                            operation={LocalPatientOperations.MedicalNote.Edit_ImagingTextChanged}
                                        />
                                    )}
                                    {(!isSignedOff || !IsNullOrWhiteSpace(encounter.ProgressNote) || notesOther.size > 0) && (
                                        <ProgressNoteSection
                                            isLongTitle
                                            title={cacheStore.GetString("OtherNotesAndInterventionsLabel")}
                                            encounterId={encounter.Id}
                                            itemSize={notesOther.size}
                                            noteContent={encounter.ProgressNote}
                                            isEditMode={props.isEditMode}
                                            isReadOnly={props.isReadOnly}
                                            sectionName="other"
                                            context="OTHN"
                                            clickableTextVisible={props.noteModel.get("OtherNotesClickableTextVisible")}
                                            showNotePopup={(context, event) => props.showNotePopup(context, event)}
                                            closeClickableText={() => props.closeClickableText()}
                                            onAutosaveFieldBlur={() => handleBlur()}
                                            showClickableVisibility={(text, event) => props.showClickableVisibility(text, event)}
                                            toggleAutoSave={() => props.toggleAutoSave()}
                                            operation={LocalPatientOperations.MedicalNote.Edit_OtherNotesTextChanged}
                                        />
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <div className="padding-sm" />
        </>
    );
}

export { ProgressNote };
